import {
  LANDING_ROADMAP_ARTBOARD_WIDTH,
  LANDING_ROADMAP_CARD_HALF_HEIGHT,
  LANDING_ROADMAP_CARD_STICKY_MIN_TOP,
  LANDING_ROADMAP_CV_DOODLE_THRESHOLDS,
  LANDING_ROADMAP_CV_FILL_TAIL,
  LANDING_ROADMAP_CV_PROGRESS_RATE,
  LANDING_ROADMAP_CV_SECTION_TOP,
  LANDING_ROADMAP_CV_STEP_LEAD,
  LANDING_ROADMAP_CV_STEP_PROGRESS,
  LANDING_ROADMAP_CV_STEPS_Y,
  LANDING_ROADMAP_CV_STROKE_THRESHOLDS,
  LANDING_ROADMAP_EXAM_STEP_DWELL_MS,
  LANDING_ROADMAP_EXAM_STEPS_Y,
  LANDING_ROADMAP_EXIT_Y,
  LANDING_ROADMAP_FIT_HEIGHT_ATTR,
  LANDING_ROADMAP_FIT_LINES_ATTR,
  LANDING_ROADMAP_FIT_MIN_SCALE,
  LANDING_ROADMAP_HORIZONTAL_MAX_TRANSLATE,
  LANDING_ROADMAP_HORIZONTAL_TOP,
  LANDING_ROADMAP_MAX_FRAME_MS,
  LANDING_ROADMAP_NODE_Y,
  LANDING_ROADMAP_PATH_A_PROGRESS,
  LANDING_ROADMAP_PATH_A_Y,
  LANDING_ROADMAP_PATH_B_PROGRESS,
  LANDING_ROADMAP_PATH_B_Y,
  LANDING_ROADMAP_REVEAL_LINE,
  LANDING_ROADMAP_REVEAL_SECTIONS,
  LANDING_ROADMAP_SECTION_TOPS,
} from '@/constants/landingRoadmap';
import type { LandingRoadmapPlayback, LandingRoadmapScrollState, LandingRoadmapValues } from '@/types/landingRoadmap';

// Tasarım dosyasındaki DCLogic.renderVals()'ın birebir karşılığı. 'static' mod, scroll edilemeyen
// ortamda her şeyi bitmiş haliyle gösterir; 'scroll' modda değerler sayfa konumundan türetilir.

const ACTIVE_NODE = 'background:#6F82F4;border-color:#6F82F4;transform:scale(1.15);';

function interpolateProgress(ys: number[], progress: number[], y: number) {
  if (y <= ys[0]) return 0;
  if (y >= ys[ys.length - 1]) return 1;
  for (let i = 1; i < ys.length; i++) {
    if (y <= ys[i]) {
      const d = ys[i] - ys[i - 1] || 1;
      return progress[i - 1] + ((progress[i] - progress[i - 1]) * (y - ys[i - 1])) / d;
    }
  }
  return 1;
}

// Artboard (1280px) daha dar ekranlarda genişliğe sığacak şekilde zoom ile küçültülür; büyütülmez.
export function getLandingRoadmapZoom() {
  return Math.min(1, document.documentElement.clientWidth / LANDING_ROADMAP_ARTBOARD_WIDTH);
}

// Scroll durumu artboard koordinatlarında döner (sayfa px'i / zoom); eğriler ve eşikler tasarım ölçüsündedir.
export function measureLandingRoadmapScroll(): LandingRoadmapScrollState {
  const scroller = document.scrollingElement || document.documentElement;
  if (scroller.scrollHeight <= window.innerHeight + 40) return { mode: 'static' };
  const zoom = getLandingRoadmapZoom();
  const vh = window.innerHeight / zoom;
  const st = scroller.scrollTop / zoom;
  return { mode: 'scroll', yt: st + vh * 0.55, st, vh };
}

// Scroll konumunun istediği animasyon durumu (CV dolumu, sınav adımı, görünür bölümler).
export function getLandingRoadmapPlaybackTarget(state: LandingRoadmapScrollState): LandingRoadmapPlayback {
  if (state.mode === 'static') {
    return { cvProgress: 1, examStep: 3, examStepElapsedMs: 0, revealed: LANDING_ROADMAP_REVEAL_SECTIONS };
  }
  const { yt, st, vh } = state;

  const c = LANDING_ROADMAP_CV_STEPS_Y;
  const stepStart2 = c[1] - LANDING_ROADMAP_CV_STEP_LEAD;
  const stepStart3 = c[2] - LANDING_ROADMAP_CV_STEP_LEAD;
  const stickyTop = Math.max(LANDING_ROADMAP_CARD_STICKY_MIN_TOP, vh / 2 - LANDING_ROADMAP_CARD_HALF_HEIGHT);
  const fullyVisibleY = Math.min(LANDING_ROADMAP_CV_SECTION_TOP - stickyTop + vh * 0.55, stepStart2 - 100);
  const [progress2, progress3] = LANDING_ROADMAP_CV_STEP_PROGRESS;
  const cvProgress = interpolateProgress(
    [fullyVisibleY, stepStart2, stepStart3, stepStart3 + LANDING_ROADMAP_CV_FILL_TAIL],
    [0, progress2, progress3, 1],
    yt
  );

  const e = LANDING_ROADMAP_EXAM_STEPS_Y;
  const examStep = yt < e[0] - 60 ? 0 : yt < (e[0] + e[1]) / 2 ? 1 : yt < (e[1] + e[2]) / 2 ? 2 : 3;

  const revealLine = st + vh * LANDING_ROADMAP_REVEAL_LINE;
  const revealed = LANDING_ROADMAP_REVEAL_SECTIONS.filter(
    (idx) => revealLine >= LANDING_ROADMAP_SECTION_TOPS[idx] + 10
  );

  return { cvProgress, examStep, examStepElapsedMs: 0, revealed };
}

// Gösterilen durumu hedefe doğru ilerletir: CV dolumu hız sınırlı, sınav adımları birer birer ve en az
// DWELL süresi aralıkla değişir; görünen bölüm bir daha gizlenmez. done=true ise hedefe ulaşılmıştır.
export function advanceLandingRoadmapPlayback(
  current: LandingRoadmapPlayback,
  target: LandingRoadmapPlayback,
  dtMs: number
): { playback: LandingRoadmapPlayback; done: boolean } {
  const maxDelta = (LANDING_ROADMAP_CV_PROGRESS_RATE * Math.min(dtMs, LANDING_ROADMAP_MAX_FRAME_MS)) / 1000;
  const cvDiff = target.cvProgress - current.cvProgress;
  const cvProgress =
    Math.abs(cvDiff) <= maxDelta ? target.cvProgress : current.cvProgress + Math.sign(cvDiff) * maxDelta;

  let examStep = current.examStep;
  let examStepElapsedMs = current.examStepElapsedMs + dtMs;
  if (examStep !== target.examStep && examStepElapsedMs >= LANDING_ROADMAP_EXAM_STEP_DWELL_MS) {
    examStep += Math.sign(target.examStep - examStep);
    examStepElapsedMs = 0;
  }

  const revealed = Array.from(new Set([...current.revealed, ...target.revealed]));

  return {
    playback: { cvProgress, examStep, examStepElapsedMs, revealed },
    done: cvProgress === target.cvProgress && examStep === target.examStep,
  };
}

export function computeLandingRoadmapValues(
  state: LandingRoadmapScrollState,
  playback: LandingRoadmapPlayback = getLandingRoadmapPlaybackTarget(state)
): LandingRoadmapValues {
  const on = state.mode === 'static';
  const yt = state.mode === 'scroll' ? state.yt : 0;
  const st0 = state.mode === 'scroll' ? state.st : 0;
  const vh = state.mode === 'scroll' ? state.vh : 800;
  const st = st0 + Math.max(0, (vh - 720) / 2);
  const v: LandingRoadmapValues = {};

  // Dikey yol A (hero -> modül girişi)
  const pA = on ? 1 : interpolateProgress(LANDING_ROADMAP_PATH_A_Y, LANDING_ROADMAP_PATH_A_PROGRESS, yt);
  v.offA = (1.004 - pA * 1.004).toFixed(4);
  v.dA = (pA * 100).toFixed(2) + '%';
  v.oA = on ? 0 : pA > 0.002 && pA < 0.998 ? 1 : 0;

  // Yol B (modül çıkışı -> CTA)
  const rel = LANDING_ROADMAP_HORIZONTAL_TOP + LANDING_ROADMAP_HORIZONTAL_MAX_TRANSLATE;
  let pB = 1;
  if (!on) {
    const ytB = Math.min(yt, LANDING_ROADMAP_EXIT_Y + 1.7 * (st - rel));
    pB = st < rel ? 0 : interpolateProgress(LANDING_ROADMAP_PATH_B_Y, LANDING_ROADMAP_PATH_B_PROGRESS, ytB);
  }
  v.offB = (1.004 - pB * 1.004).toFixed(4);
  v.dB = (pB * 100).toFixed(2) + '%';
  v.oB = on ? 0 : pB > 0.002 && pB < 0.998 ? 1 : 0;

  // Yol haritası düğümleri
  LANDING_ROADMAP_NODE_Y.forEach((y, i) => {
    v['a' + i] = on || yt >= y ? ACTIVE_NODE : '';
  });
  v.aN = on || pA >= 0.999 ? ACTIVE_NODE : '';
  v.gB = on || st >= rel ? 1 : 0;
  v.bx = on || pB > 0.001 ? ACTIVE_NODE : '';
  v.nE = on || pB >= 0.999 ? 'background:#9AA6FF;transform:scale(1.18);' : '';

  // Bölüm görünme sarmalayıcıları (0,2,3,4)
  LANDING_ROADMAP_REVEAL_SECTIONS.forEach((idx) => {
    const hit = on || playback.revealed.includes(idx);
    v['s' + idx] = hit ? 'opacity:1;transform:none;' : 'opacity:0;transform:translateY(32px);';
  });

  // Profil oluşturucu (CV): adım, dolum ilerlemesinden türetilir; böylece metin vurgusu ve CV görüntüsü
  // birlikte ve sırayla değişir.
  const [progress2, progress3] = LANDING_ROADMAP_CV_STEP_PROGRESS;
  const builderProgress = playback.cvProgress;
  if (on) {
    const anim = (name: string, seconds: number) => 'animation:' + name + ' ' + seconds + 's linear infinite;';
    v.bar = 'width:48%;' + anim('bw', 18);
    v.v1 = anim('bv1', 18);
    v.v2 = 'opacity:0;' + anim('bv2', 18);
    v.v3 = 'opacity:0;' + anim('bv3', 18);
    v.n1 = 'opacity:0;' + anim('nt12', 18);
    v.n2 = 'opacity:0;' + anim('nt12', 18);
    v.n3 = 'opacity:0;' + anim('nt3', 18);
    v.c0 = anim('ch1', 18);
    v.c1 = 'opacity:.45;' + anim('ch2', 18);
    v.c2 = 'opacity:.45;' + anim('ch3', 18);
    v.hl = '100%';
  } else {
    const step = builderProgress < progress2 ? 1 : builderProgress < progress3 ? 2 : 3;
    const cstep = step;
    const percent = { 1: 48, 2: 82, 3: 94 }[step];
    v.bar = 'width:' + percent + '%;';
    v.hl = step > 1 ? '100%' : '0%';
    v.v1 = 'opacity:' + (step === 1 ? 1 : 0) + ';';
    v.v2 = 'opacity:' + (step === 2 ? 1 : 0) + ';';
    v.v3 = 'opacity:' + (step === 3 ? 1 : 0) + ';';
    const note = (visible: boolean) => (visible ? 'opacity:1;transition:.55s;' : 'opacity:0;transition:.55s;');
    v.n1 = note(step >= 2);
    v.n2 = note(step >= 2);
    v.n3 = note(step >= 3);
    v.c0 = 'opacity:' + (cstep === 1 ? 1 : 0.45) + ';';
    v.c1 = 'opacity:' + (cstep === 2 ? 1 : 0.45) + ';';
    v.c2 = 'opacity:' + (cstep === 3 ? 1 : 0.45) + ';';
  }

  // CV karalamaları: oluşturucu ilerlemesine göre sırayla belirir
  const doodleProgress = on ? 1 : builderProgress;
  LANDING_ROADMAP_CV_DOODLE_THRESHOLDS.forEach((threshold, i) => {
    const ok = doodleProgress >= threshold;
    v['f' + i] = ok ? 'opacity:1;transform:none;' : 'opacity:0;transform:translateY(6px);';
    v['g' + i] = ok ? 'opacity:0;' : 'opacity:1;';
  });
  LANDING_ROADMAP_CV_STROKE_THRESHOLDS.forEach((threshold, i) => {
    v['k' + i] =
      doodleProgress >= threshold
        ? 'opacity:1;stroke-dashoffset:0;transform:none;'
        : 'opacity:0;stroke-dashoffset:1;transform:translateY(5px);';
  });

  // Sınav modülü
  const xs = on ? 3 : playback.examStep;
  const selected = '--sel:#2B44F5;--selb:#2B44F5;--sbg:#E4E9FF;--dot:1;';
  const unselected = '--sel:rgba(31,42,85,.28);--selb:transparent;--sbg:#EEF1FA;--dot:0;';
  for (let i = 1; i <= 3; i++) {
    const dim = xs + 1 === i || (xs === 0 && i === 1) || xs >= i ? '' : 'opacity:.55;';
    v['q' + i] = (xs >= i ? selected : unselected) + dim + 'transition:opacity .4s;';
  }
  v.xbar = 'width:' + [6, 33, 66, 100][xs] + '%;';
  for (let i = 0; i <= 3; i++) v['xc' + i] = 'opacity:' + (xs === i ? 1 : 0) + ';transition:opacity .3s;';
  for (let i = 1; i <= 3; i++) v['w' + i] = 'opacity:' + (Math.max(1, xs) === i ? 1 : 0) + ';transition:opacity .3s;';
  v.xh = 'opacity:' + (xs === 3 ? 0 : 1) + ';';
  v.xd = xs === 3 ? 'opacity:1;transform:none;' : 'opacity:0;transform:translateY(10px);';
  const examStep = Math.max(1, xs);
  for (let i = 0; i < 3; i++) v['x' + i] = 'opacity:' + (on || examStep === i + 1 ? 1 : 0.45) + ';';

  // Yatay modül şeridi
  const maxT = LANDING_ROADMAP_HORIZONTAL_MAX_TRANSLATE;
  const entryHit = on || pA >= 0.999;
  const q = on ? 0 : Math.max(0, Math.min(1, (st - LANDING_ROADMAP_HORIZONTAL_TOP) / maxT));
  const viewportEdge = LANDING_ROADMAP_ARTBOARD_WIDTH * 0.8;
  v.track = on
    ? 'animation:slideH 34s ease-in-out infinite alternate;'
    : 'transform:translateX(' + (-q * maxT).toFixed(1) + 'px);';
  const reach = on ? maxT : Math.max(0, Math.min(1, (q * maxT + viewportEdge - 640) / maxT));
  v.hOff = (1.004 - (entryHit ? reach : 0) * 1.004).toFixed(4);
  for (let k = 0; k < 6; k++) {
    const nx = 640 + k * (maxT / 5);
    v['h' + k] = on || (entryHit && nx <= q * maxT + viewportEdge) ? ACTIVE_NODE : '';
  }
  v.hn = '0' + Math.max(1, Math.min(6, Math.round(q * 5) + 1));
  v.hp = 'width:' + (on ? 100 : (q * 100).toFixed(1)) + '%;';

  return v;
}

export function resolveLandingRoadmapTemplate(template: string, values: LandingRoadmapValues) {
  return template.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, key: string) => String(values[key] ?? ''));
}

// Sığdırma öncesi tasarımın satır içi stili; her çalıştırmada önce buna dönülür (font/min-height gibi değerler kısaltma
// özelliklerin parçası olduğundan tek tek kaldırmak orijinal değeri de siler).
const originalFitStyles = new WeakMap<HTMLElement, string>();

function restoreOriginalStyle(el: HTMLElement) {
  const original = originalFitStyles.get(el);
  if (original === undefined) originalFitStyles.set(el, el.getAttribute('style') ?? '');
  else el.setAttribute('style', original);
}

// İşaretli öğeleri aktif dildeki metinle tasarımdaki kutularına sığdırır (yeniden çağrılabilir).
// data-lr-fit-lines: yazı boyutu, öğe en fazla o kadar satır olana kadar küçültülür.
// data-lr-fit-height: kart görsel olarak o yüksekliği aşıyorsa zoom ile orantılı küçültülür; genişlik ve minimum yükseklik
// zoom'a göre telafi edilir, böylece kart tasarımdaki ölçüde kalır, içerik biraz daha küçük görünür.
// Ölçümler artboard zoom'undan arındırılır (getBoundingClientRect görsel, yani zoom'lu boyut döner).
export function fitLandingRoadmapContent(root: HTMLElement) {
  const zoom = getLandingRoadmapZoom();
  const designHeight = (el: HTMLElement) => el.getBoundingClientRect().height / zoom;

  root.querySelectorAll<HTMLElement>(`[${LANDING_ROADMAP_FIT_LINES_ATTR}]`).forEach((el) => {
    restoreOriginalStyle(el);
    const lines = Number(el.getAttribute(LANDING_ROADMAP_FIT_LINES_ATTR));
    const baseSize = parseFloat(getComputedStyle(el).fontSize);
    const fits = () => designHeight(el) <= lines * parseFloat(getComputedStyle(el).lineHeight) + 1;
    let size = baseSize;
    while (!fits() && size > baseSize * LANDING_ROADMAP_FIT_MIN_SCALE) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
  });

  root.querySelectorAll<HTMLElement>(`[${LANDING_ROADMAP_FIT_HEIGHT_ATTR}]`).forEach((el) => {
    restoreOriginalStyle(el);
    const target = Number(el.getAttribute(LANDING_ROADMAP_FIT_HEIGHT_ATTR));
    const baseWidth = el.getBoundingClientRect().width / zoom;
    let scale = 1;
    while (designHeight(el) > target + 0.5 && scale > LANDING_ROADMAP_FIT_MIN_SCALE) {
      scale = Math.round((scale - 0.01) * 100) / 100;
      el.style.setProperty('zoom', String(scale));
      el.style.width = `${baseWidth / scale}px`;
      el.style.minHeight = `${target / scale}px`;
    }
  });
}
